/**
 * `ChartTip` — the single tooltip node §F2 gives every chart ("tooltips via shared
 * `ChartTip` portal"), i.e. the mockup's `<div class="charttip" id="charttip">`.
 *
 * Mounted **once**, next to `<Toast/>` in the app shell; charts never render it, they only
 * call `showChartTip` / `hideChartTip` from `./chartTipStore` (which is where the store and the
 * reasoning behind it live). A portal on `document.body` keeps it out of every widget's
 * stacking and clipping context.
 */
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { readChartTip, subscribeChartTip } from './chartTipStore';

export function ChartTip() {
  const state = useSyncExternalStore(subscribeChartTip, readChartTip, readChartTip);
  // The portal target only exists after the first client render, so it is resolved in an
  // effect rather than at module scope (which would also break the vitest jsdom setup).
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => setHost(document.body), []);
  const node = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null);

  /*
    Kept inside the viewport (#749): the Analysis dock sits against the right edge, so a
    popup always opened to the right of the pointer ran off the screen there. It flips to
    the pointer's left when it would overflow, and is held below the top and above the
    bottom edge.
  */
  useLayoutEffect(() => {
    if (!state || !node.current) {
      setPlace(null);
      return;
    }
    const box = node.current.getBoundingClientRect();
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    let left = state.x + 14;
    if (vw > 0 && box.width > 0 && left + box.width > vw - 4) left = Math.max(4, state.x - 14 - box.width);
    let top = state.y - 10;
    if (vh > 0 && box.height > 0 && top + box.height > vh - 4) top = Math.max(4, vh - 4 - box.height);
    top = Math.max(4, top);
    setPlace((prev) => (prev && prev.left === left && prev.top === top ? prev : { left, top }));
  }, [state]);

  if (!host || !state) return null;

  return createPortal(
    <div
      ref={node}
      className="charttip"
      role="status"
      style={place ?? { left: state.x + 14, top: state.y - 10 }}
    >
      <span className="ct-label">{state.label}</span>
      {state.rows && state.rowsOnly ? null : <b>{state.value}</b>}
      {state.rows ? (
        <ul className="ct-rows">
          {state.rows.map((row, i) => (
            <li key={i}>
              {row.color ? <i className="ct-swatch" style={{ background: row.color }} /> : null}
              <span className="ct-row-label">{row.label}</span>
              {row.value ? <b>{row.value}</b> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>,
    host,
  );
}

export default ChartTip;
